import { type Theme, useTheme } from "@mui/material";
import { useMemo } from "react";
import { useReportingQuery } from "web_myplann/Api/Finances/useReportingQuery";
import { useIsDashboardV2 } from "web_myplann/Dashboard/State/dashboardVariantContext";
import { PortfolioReportingOverviewTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import { Colors } from "web_myplann/Infrastructure/Themes/Colors";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type { DashboardType } from "../Components/DashboardTitleCard";

type DashboardConfig = {
	type: PortfolioReportingOverviewTypeEnum;
	gradientFrom: string;
	gradientTo: string;
};

const dashboardConfigs: DashboardConfig[] = [
	{
		type: PortfolioReportingOverviewTypeEnum.Total,
		gradientFrom: Colors.DashboardGradientTotalFrom,
		gradientTo: Colors.DashboardGradientTotalTo,
	},
	{
		type: PortfolioReportingOverviewTypeEnum.Investment,
		gradientFrom: Colors.DashboardGradientInvestmentFrom,
		gradientTo: Colors.DashboardGradientInvestmentTo,
	},
	{
		type: PortfolioReportingOverviewTypeEnum.InvestmentProperty,
		gradientFrom: Colors.DashboardGradientInvestmentPropertyFrom,
		gradientTo: Colors.DashboardGradientInvestmentPropertyTo,
	},
	{
		type: PortfolioReportingOverviewTypeEnum.Cash,
		gradientFrom: Colors.DashboardGradientCashFrom,
		gradientTo: Colors.DashboardGradientCashTo,
	},
	{
		type: PortfolioReportingOverviewTypeEnum.Other,
		gradientFrom: Colors.DashboardGradientOtherFrom,
		gradientTo: Colors.DashboardGradientOtherTo,
	},
];

const getPilotDashboardConfigs = (
	gradients: Theme["dashboard"]["gradients"],
): DashboardConfig[] => [
	{
		type: PortfolioReportingOverviewTypeEnum.Total,
		gradientFrom: gradients.total.from,
		gradientTo: gradients.total.to,
	},
	{
		type: PortfolioReportingOverviewTypeEnum.Investment,
		gradientFrom: gradients.investment.from,
		gradientTo: gradients.investment.to,
	},
	{
		type: PortfolioReportingOverviewTypeEnum.InvestmentProperty,
		gradientFrom: gradients.investmentProperty.from,
		gradientTo: gradients.investmentProperty.to,
	},
	{
		type: PortfolioReportingOverviewTypeEnum.Cash,
		gradientFrom: gradients.cash.from,
		gradientTo: gradients.cash.to,
	},
	{
		type: PortfolioReportingOverviewTypeEnum.Other,
		gradientFrom: gradients.other.from,
		gradientTo: gradients.other.to,
	},
];

const OverviewTypeResources = Resources.Dashboard.OverviewType;

export const useDashboardData = () => {
	const { t } = useResource();
	const { data: reportingData, isLoading, isFetching } = useReportingQuery();
	const isDashboardV2 = useIsDashboardV2();
	const { dashboard } = useTheme();

	const dashboards = useMemo<DashboardType[]>(() => {
		const items = reportingData?.data;
		const configs = isDashboardV2
			? getPilotDashboardConfigs(dashboard.gradients)
			: dashboardConfigs;

		return configs.flatMap((config, index) => {
			const apiItem = items?.find((item) => item.type === config.type);
			if (!apiItem) return [];

			const itemCount = apiItem.itemCount ?? 0;
			const itemCountWithoutValue = apiItem.itemCountWithoutValue ?? 0;
			const value = apiItem.value ?? 0;
			const nettoValue = apiItem.nettoValue;
			const hasNetValue = nettoValue != null && nettoValue !== value;

			if (value === 0 && itemCount === 0 && !hasNetValue) {
				return [];
			}

			return [
				{
					id: index,
					overviewType: config.type,
					type: t(OverviewTypeResources[config.type]),
					value: apiItem.value ?? null,
					itemCount,
					itemCountWithoutValue,
					netValue: hasNetValue ? nettoValue : undefined,
					currency: apiItem.currency ?? "",
					evaluation: apiItem.evaluation ?? undefined,
					gradientFrom: config.gradientFrom,
					gradientTo: config.gradientTo,
				},
			];
		});
	}, [reportingData, t, isDashboardV2, dashboard.gradients]);

	const isEmpty = dashboards.length === 0;

	return { dashboards, isLoading, isFetching, isEmpty };
};

export const getDashboardIndexByType = (
	dashboards: DashboardType[],
	type: PortfolioReportingOverviewTypeEnum,
) => dashboards.findIndex((d) => d.overviewType === type);
